import { useContext, useEffect, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { Container, Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { connect } from "react-redux";
import { saveTicketSelectedFilters } from "../../store/actions";
import { LocaleContext } from "../../routing/LangRouter";
import useHeaderInfo from "../../hooks/useHeaderInfo";

//import constants
import { IS_MOBILE } from "../../constants/breakpoints";
import BASE_URL from "../../constants/baseUrls";

//import components
import BusSearchForm from "../../components/view/bus/bus-search-form";
import BusList from "../../components/view/bus/bus-list";
import EmptyOrErrorState from "../../components/common/empty-or-error-state/empty-or-error-state";
import Loading from "../../components/common/loading/loading";
import BaseSort from "../../components/common/base-sort/base-sort";
import BusFilter from "../../components/view/bus/bus-filter";
import ExpirationResultModal from "../../components/common/expiration-result-modal/expiration-result-modal";
import BaseTitle from "../../components/common/base-title/base-title";
import DayChange from "../../components/common/day-change/day-change";
import MobileHeader from "../../components/common/mobile-header/mobile-header";

//import icons
import { Edit2, InfoCircle, Warning2 } from "iconsax-react";

function BusPage({ dispatch, ticketSelectedFilters }) {
    const { locale } = useContext(LocaleContext);
    const [searchParams, setSearchParams] = useSearchParams();
    const { t } = useTranslation();
    const accessToken = localStorage.getItem("accessToken");

    const busInformation = JSON.parse(sessionStorage.getItem("busInformation"));

    const [openSearchForm, setOpenSearchForm] = useState(false);
    const [uid, setUid] = useState();
    const [requestFlag, setRequestFlag] = useState(false);
    const [isSortOpen, setIsSortOpen] = useState(false);
    const [isFilterOpen, setIsFilterOpen] = useState(false);

    //list of sorting options
    const optionListSort = [
        { value: "4", label: `${t("cheapest")}`, checked: false },
        { value: "3", label: `${t("expensive")}`, checked: false },
        { value: "1", label: `${t("earliest")}`, checked: true },
        { value: "2", label: `${t("lastest")}`, checked: false },
    ];

    //set value radio button for sort
    const [selectedSort, setSelectedSort] = useState("4");
    const handleSlectedRadio = value => {
        setSelectedSort(value);
        setRequestFlag(true);
    };

    useEffect(() => {
        if (searchParams.get("action") === "edit") setOpenSearchForm(true);
        else setOpenSearchForm(false);
    }, [searchParams]);

    //get list
    const [busList, setBusList] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isError, setIsError] = useState(false);
    const [requestExpirationDate, setRequestExpirationDate] = useState("");
    const [busFilters, setBusFilters] = useState();
    const [usedFilters, setUsedFilters] = useState();

    const getBusList = async () => {
        try {
            setIsLoading(true);
            const response = await axios.post(
                `${BASE_URL}/api/v2/reserve/bus/list/`,
                {
                    origin: busInformation?.departure?.id,
                    destination: busInformation?.destination?.id,
                    move_date: busInformation?.departureDate,
                    uid: uid,
                    sort: selectedSort,

                    move_time_from: ticketSelectedFilters?.move_times?.[0],
                    move_time_to: ticketSelectedFilters?.move_times?.[1],
                    price_from: ticketSelectedFilters?.prices?.[0],
                    price_to: ticketSelectedFilters?.prices?.[1],
                },
                {
                    headers: {
                        Authorization: accessToken ? `Bearer ${accessToken}` : "",
                    },
                },
            );
            setIsError(false);
            setBusList(response?.data?.data?.results);
            setUid(response?.data?.data?.uid);
            setRequestExpirationDate(response?.data?.data?.uid_expires_at);
            setBusFilters(response?.data?.data?.available_filters);

            const params = response?.data?.params;
            setUsedFilters({ prices: [params?.price_from, params?.price_to], move_times: [params?.move_time_from, params?.move_time_to] });
        } catch (error) {
            setIsError(true);
        } finally {
            setIsLoading(false);
            setRequestFlag(false);
        }
    };

    useEffect(() => {
        getBusList();
    }, []);

    useEffect(() => {
        if (requestFlag) getBusList();
    }, [requestFlag]);

    //handle filter cancellation
    const resetFilters = () => {
        dispatch(saveTicketSelectedFilters(null));
    };

    const { headerTitle, headerDetails } = useHeaderInfo("bus", busInformation);

    const navigateToHome = () => {
        Navigate(`/${locale}`);
    };

    //handle show edit search form and flight details
    const handleEdittSearch = action => {
        const params = { action };
        setSearchParams(params, { replace: true });
    };

    if (isLoading) return <Loading />;

    return (
        <Grid
            container
            item
            xs={12}
            justifyContent="center"
            rowGap={{ xs: 2, md: 5 }}
            mt={openSearchForm || IS_MOBILE ? 8 : 0}
            mb={{ xs: 10, md: 5 }}
            className="bus-page"
        >
            <MobileHeader
                title={headerTitle}
                details={headerDetails}
                edit={<Edit2 size="20" color="var(--primary-color)" onClick={() => handleEdittSearch("edit")} />}
                justifyContent="flex-start"
                isButton={false}
                handleClick={navigateToHome}
            />
            <BusSearchForm setRequestFlag={setRequestFlag} openSearchForm={openSearchForm} setUid={setUid} />
            <Container maxWidth="xl" className="bus-list">
                {isError ? (
                    <EmptyOrErrorState
                        title={t("errorMessage")}
                        handleClick={getBusList}
                        actionLabel={t("retry")}
                        icon={<Warning2 size="32" color="var(--red-color)" variant="Bold" />}
                    />
                ) : !busList?.available_list?.length && !busList?.full_list?.length ? (
                    <EmptyOrErrorState title={t("busListMessage")} icon={<InfoCircle size="32" color="var(--secondary-color)" variant="Bold" />} />
                ) : (
                    <>
                        <BaseTitle
                            title={`${t("busList")}:`}
                            dividerColor="1px solid var(--medium-gray-color)"
                            button={
                                <DayChange
                                    label="busInformation"
                                    information={busInformation}
                                    date={busInformation?.departureDate}
                                    isDeparture={true}
                                    setRequestFlag={setRequestFlag}
                                    setUid={setUid}
                                />
                            }
                        />

                        <Grid container item xs={12} justifyContent="space-between" alignItems="flex-start" mt={2}>
                            <Grid container item xs={2}>
                                {!!busList?.available_list?.length && (
                                    <BusFilter
                                        filterListData={busFilters}
                                        usedFilters={usedFilters}
                                        open={isFilterOpen}
                                        setOpen={setIsFilterOpen}
                                        setRequestFlag={setRequestFlag}
                                        setUid={setUid}
                                    />
                                )}
                            </Grid>
                            <Grid container item md={9.8} rowGap={4}>
                                <BaseSort
                                    open={isSortOpen}
                                    setOpen={setIsSortOpen}
                                    optionList={optionListSort}
                                    handleChange={handleSlectedRadio}
                                    defaultValue={selectedSort}
                                    setRequestFlag={setRequestFlag}
                                />
                                <BusList list={busList?.available_list} uid={uid} requestExpirationDate={requestExpirationDate} />
                                <BusList list={busList?.full_list} uid={uid} requestExpirationDate={requestExpirationDate} />
                            </Grid>
                        </Grid>
                    </>
                )}
            </Container>

            <ExpirationResultModal
                requestExpirationDate={requestExpirationDate}
                setRequestFlag={setRequestFlag}
                uid={uid}
                setUid={setUid}
                resetList={() => setBusList([])}
                resetFilters={resetFilters}
            />
        </Grid>
    );
}
export default connect(mapStateToProps)(BusPage);
function mapStateToProps(state) {
    return {
        ticketSelectedFilters: state.ticketSelectedFilters,
    };
}
